import type { FileInfo } from '@puppycad/proto'
import {
  CompleteGlbOccurrenceGraphAnalyzer,
  InstanceIdCompleteGlbRootNodeSelectionStrategy,
  composeProjectModelImportedResult,
  createProjectModelLoadPlan,
  disposeImportedModel,
  executeProjectModelLoad,
  parseGlbJsonDocument,
} from '@puppycad/cad-core'
import type { ModelAssetFile, ModelAssetNode, ProjectModelPackageResult } from '@puppycad/cad-core'
import { CompleteGlbPackageReader } from '../project-model-loading/complete-glb-package'
import { loadCompleteGlbScene } from './imported-model-loader'
import type { LoadedImportedModel } from './imported-model-loader'
import { throwIfImportedModelRequestStale } from './imported-model-load-guard'
import { mapWithConcurrencyLimit } from './map-with-concurrency-limit'
import { parseInsertedCadRuntimePackage } from './insert-cad-runtime-package'

/** 插入事务内的资产版本，不跨项目复用可变场景。 */
const INSERT_RUNTIME_PACKAGE_CACHE_VERSION = 'insert-cad-runtime-package-one-shot'
/** 限制同时解析和注册的 occurrence 数量，避免大装配瞬时内存放大。 */
const MAX_INSERT_NODE_PACKAGE_LOAD_CONCURRENCY = 4

/**
 * 收集模型资产树内的所有节点。
 *
 * @param node 当前节点。
 * @returns 前序节点列表。
 */
function collectModelAssetNodesPreorder(node: ModelAssetNode): readonly ModelAssetNode[] {
  return [node, ...node.children.flatMap(child => collectModelAssetNodesPreorder(child))]
}

/**
 * 按清单 occurrence 身份从完整 GLB 中加载唯一子树。
 *
 * 这里只传递身份与资源依赖；根筛选、根位姿归一化、拓扑所有权及 BRep 绑定统一由 SDK
 * complete 链路处理。禁止按名字或 definition 去重，也不能在匹配失败时装入整个 GLB。
 *
 * @param input 当前插入事务依赖。
 * @param node 清单中真实引用包的节点，保留 instanceUuid 与局部放置语义。
 * @param file 节点引用的包文件。
 * @param snapshot 同包共享的只读文件系统。
 * @returns 当前节点独占的运行时结果。
 */
async function loadNodePackageResult(
  input: PuppyCADWeb.LoadInsertedCadRuntimePackageModelInput,
  node: ModelAssetNode,
  file: ModelAssetFile,
  snapshot: PuppyCADWeb.InsertCadNodePackageSnapshot,
): Promise<ProjectModelPackageResult> {
  try {
    const instanceId = node.instanceUuid?.trim()
    if (!instanceId) {
      throw new Error('插入零部件节点缺少 instance_id，无法确定包内实例范围')
    }
    const rootSelection = new InstanceIdCompleteGlbRootNodeSelectionStrategy(instanceId)
      .select(snapshot.rawJson)
    const occurrenceGraph = new CompleteGlbOccurrenceGraphAnalyzer().analyze({
      json: snapshot.rawJson,
      selection: rootSelection,
    })
    // 文件路径表示可共享的资源；根身份表示本次加载范围，二者不能混为缓存键或树所有权。
    const packageFile: ModelAssetFile = {
      ...file,
      loadMode: 'complete-glb-scene',
      completeGlbRootInstanceId: instanceId,
    }
    const loaded = await loadCompleteGlbScene(input.sessionId, packageFile, snapshot.sourceGlbFile, {
      projectFiles: snapshot.projectFiles,
      version: INSERT_RUNTIME_PACKAGE_CACHE_VERSION,
      expectedSemanticKind: node.nodeType,
      rootSelection,
      occurrenceGraph,
      assemblyModelCache: input.assemblyModelCache,
      partAssetCache: input.partAssetCache,
      parsedGlbCache: input.parsedGlbCache,
      importedBrepRuntimeCache: input.importedBrepRuntimeCache,
      brepRegistrar: input.brepRegistrar,
      readFileBytes: snapshot.readFileBytes,
      transaction: input.transaction,
      guard: { isStale: () => input.transaction.isAborted },
    })
    return { nodeKey: node.nodeKey, file: packageFile, result: loaded.result }
  } catch (error) {
    const reason = error instanceof Error ? error.message : String(error)
    throw new Error(
      `插入零部件实例加载失败：nodeKey=${node.nodeKey}, instanceId=${node.instanceUuid || 'missing'}, fileId=${file.fileId}; ${reason}`,
      { cause: error },
    )
  }
}

/**
 * 创建插入运行时包的根 FileInfo。
 *
 * @param input 加载输入。
 * @returns 组合结果使用的根文件描述。
 */
function createRuntimePackageRootFileInfo(input: PuppyCADWeb.LoadInsertedCadRuntimePackageModelInput): FileInfo {
  return {
    fileId: input.selectedFileId,
    uuid: `insert-runtime-root:${input.selectedFileId}`,
    kind: input.kind === 'part' ? '.part.cad' : '.assembly.cad',
    displayName: input.displayName,
    size: BigInt(input.bytes.byteLength),
  } as unknown as FileInfo
}

/**
 * 加载插入运行时包，按 occurrence 组合独立场景子树。
 *
 * 包字节、解压文件和原始 JSON 在本次请求中共享；GLTF 运行时对象由 complete 加载器按
 * occurrence 独立创建。失败时等待所有在途任务结束再释放，避免晚完成结果逃逸到事务外。
 *
 * @param input 运行时包加载输入。
 * @returns 已组合的导入模型。
 */
export async function loadInsertedCadRuntimePackageModel(
  input: PuppyCADWeb.LoadInsertedCadRuntimePackageModelInput,
): Promise<LoadedImportedModel> {
  const guard = { isStale: () => input.transaction.isAborted }
  const assertCurrent = (): void => throwIfImportedModelRequestStale(guard)
  assertCurrent()
  const parsedPackage = await parseInsertedCadRuntimePackage({
    bytes: input.bytes,
    displayName: input.displayName,
    fileId: input.selectedFileId,
    kind: input.kind,
  })
  const loadPlan = createProjectModelLoadPlan({ kind: 'root-load', root: parsedPackage.root })
  const projectModelLoadResult = await executeProjectModelLoad({
    root: parsedPackage.root,
    reader: parsedPackage.reader,
    plan: loadPlan,
  })
  const packageLoadTasks = collectModelAssetNodesPreorder(projectModelLoadResult.root)
    .flatMap(node => node.packageFiles.map(packageFile => ({ node, packageFile })))
  const snapshots = new Map<string, Promise<PuppyCADWeb.InsertCadNodePackageSnapshot>>()
  const completedPackageResults: ProjectModelPackageResult[] = []
  const errors: unknown[] = []

  /**
   * 按真实包身份共享读取/解压 promise，不能按节点重复解压同一个完整装配。
   *
   * @param file 当前节点引用的包。
   * @returns 同包复用的只读快照。
   */
  function readPackage(file: ModelAssetFile): Promise<PuppyCADWeb.InsertCadNodePackageSnapshot> {
    const key = JSON.stringify([file.fileId, file.uuid])
    const cached = snapshots.get(key)
    if (cached) return cached
    const pending = (async () => {
      assertCurrent()
      const bytes = await parsedPackage.reader.readAll(file)
      const files = await new CompleteGlbPackageReader().read({ packageFile: file, bytes, assertCurrent })
      const rawJson = parseGlbJsonDocument(await files.readFileBytes(files.sourceGlbFile))
      assertCurrent()
      return { ...files, rawJson }
    })()
    snapshots.set(key, pending)
    return pending
  }

  try {
    const results = await mapWithConcurrencyLimit(
      packageLoadTasks,
      MAX_INSERT_NODE_PACKAGE_LOAD_CONCURRENCY,
      async (task) => {
        if (errors.length > 0) return null
        try {
          assertCurrent()
          const snapshot = await readPackage(task.packageFile)
          const result = await loadNodePackageResult(input, task.node, task.packageFile, snapshot)
          completedPackageResults.push(result)
          return result
        } catch (error) {
          // 先记录失败但不让并发调度提前 reject；其它已启动任务必须完成后才能统一释放。
          errors.push(error)
          return null
        }
      },
    )
    if (errors.length > 0) throw errors[0]
    assertCurrent()
    const packageResults = results.filter((result): result is ProjectModelPackageResult => result !== null)
    const composedResult = composeProjectModelImportedResult({
      root: projectModelLoadResult.root,
      packageResults,
      partName: input.displayName,
      fileInfo: createRuntimePackageRootFileInfo(input),
    })
    return {
      displayType: input.kind,
      result: composedResult,
      partAssetCache: input.partAssetCache,
      importedBrepRuntimeCache: input.importedBrepRuntimeCache,
    }
  } catch (error) {
    for (const packageResult of completedPackageResults) disposeImportedModel(packageResult.result)
    throw error
  } finally {
    // 此缓存不进入项目或全局状态；结果接管后只保留正式模型持有的资源与拓扑源。
    snapshots.clear()
  }
}
